<script setup>
import moment from 'moment'
import { useAbility } from '@casl/vue'

import CommitteeService from '@/services/quality/committee.service'
import CommitteeActionTable from '@/views/quality/committee/CommitteeActionTable.vue'
import ActionCloseDialog from '@/views/quality/committee/ActionCloseDialog.vue'
import { CommitteeActionStatus, committeeActionStatusTitle } from '@/enums/quality/committee-action-status'

// Short CASL subject. A 4-part permission (quality.committee.menu.actions)
// drops its module segment in User::getAbilitiesAttribute(), so the subject
// is 'committee.menu' with action 'actions' — matching
// MeetingActionsSection.vue's canViewActions check and the guard the router
// already runs against this same page meta.
definePage({
  meta: {
    subject: 'committee.menu',
    action: 'actions',
  },
})

const { can } = useAbility()

const canClose = computed(() => can('close_action', 'quality.committee'))

// ── Filter options ───────────────────────────────────
const statusOptions = Object.values(CommitteeActionStatus).map(value => ({
  title: committeeActionStatusTitle(value),
  value,
}))

// Committees — for the filter select. NOT unit-scoped: CommitteeService::index()
// (backend) ignores unit_id entirely, so passing it here would read as working
// scoping while doing nothing — the meetings page's committee-dropdown fetch
// does exactly that today; not copied here.
const committees = ref([])

CommitteeService.list({ per_page: 200 }, data => {
  committees.value = data?.data ?? data ?? []
})

// Assignees — masters/users, id/name only, same lightweight roster pattern
// RiskPromoteDialog.vue and Rca.vue already use elsewhere in this module. A
// failure here only narrows a filter dropdown, not the tracker's primary
// read, so it degrades to an empty list rather than needing its own error
// state.
const { data: usersData } = useApi(createUrl('/masters/users', {
  query: { showAll: true, select: ['id', 'name'] },
}))

const assignees = computed(() => usersData.value?.data ?? usersData.value ?? [])

// ── Filters state ────────────────────────────────────
const committeeId = ref(null)
const status = ref(null)
const assigneeId = ref(null)
const overdueOnly = ref(false)
const searchQuery = ref('')

// Date range (single field, range mode) — mirrors the meetings page's
// convention over target_date rather than two separate pickers.
const dateRange = ref(null)
const fromDate = ref(null)
const toDate = ref(null)

const dateRangeConfig = ref({
  mode: 'range',
  dateFormat: 'Y-m-d',
  enableTime: false,
  onChange: selectedDates => {
    if (selectedDates.length > 0) {
      fromDate.value = moment(selectedDates[0]).format('YYYY-MM-DD')
      toDate.value = selectedDates[1]
        ? moment(selectedDates[1]).format('YYYY-MM-DD')
        : null
    } else {
      fromDate.value = null
      toDate.value = null
    }
  },
})

const debouncedSearch = useDebounceFn(val => {
  searchQuery.value = val
  page.value = 1
}, 500)

const filters = computed(() => ({
  committeeId: committeeId.value,
  status: status.value,
  assigneeId: assigneeId.value,
  overdueOnly: overdueOnly.value,
  from: fromDate.value,
  to: toDate.value,
  search: searchQuery.value,
}))

// ── Table state ──────────────────────────────────────
const itemsPerPage = ref(15)
const page = ref(1)

const actions = ref([])
const totalActions = ref(0)
const isFetching = ref(false)

// Distinct from `empty` — useApi's onError never fires globally, so a failed
// fetch has to be carried explicitly or it renders as an empty tracker: every
// commitment in the organisation silently missing.
const hasFailed = ref(false)

const buildQuery = () => ({
  committee_id: committeeId.value || undefined,

  // Array when non-empty (matches the meetings page's multi-status select);
  // omitted entirely otherwise so `open` below is free to take over.
  status: status.value?.length ? status.value : undefined,
  assignee_id: assigneeId.value || undefined,

  // Independent of `overdue` server-side — both may be sent together.
  // `open` is sent ONLY while no explicit status is selected: this is what
  // makes the tracker default to open actions, and it must not fight a
  // deliberate status filter (e.g. filtering to COMPLETED would be
  // contradicted by an `open=1` sent alongside it).
  open: status.value?.length ? undefined : 1,
  overdue: overdueOnly.value ? 1 : undefined,
  from: fromDate.value || undefined,
  to: toDate.value || undefined,
  search: searchQuery.value || undefined,
  per_page: itemsPerPage.value,
  page: page.value,

  // Deliberately no unit_id — CommitteeService::index() ignores it and unit
  // scoping is already handled by the global UnitScope. Sending it here
  // would read as working scoping while doing nothing.
})

// Monotonic token guarding against out-of-order responses: filter changes,
// search and pagination each fire an independent request with no
// cancellation, so a slow earlier response could otherwise land after a
// faster later one and silently overwrite correct rows with stale ones.
let fetchToken = 0

const fetchActions = async () => {
  const token = ++fetchToken

  isFetching.value = true
  hasFailed.value = false

  await CommitteeService.listActions(buildQuery(), data => {
    if (token !== fetchToken) return
    actions.value = data?.data ?? []
    totalActions.value = data?.meta?.total ?? 0
    isFetching.value = false
  }, () => {
    if (token !== fetchToken) return

    // Never fall through to [] — see hasFailed's declaration above.
    hasFailed.value = true
    isFetching.value = false
  })
}

// Any filter change (including debounced search) resets to page 1. If we're
// already on page 1 that reset is a no-op, so fetch directly; otherwise let
// the page watcher below fire the fetch once the reset lands.
watch(filters, () => {
  if (page.value === 1)
    fetchActions()
  else
    page.value = 1
})

watch([page, itemsPerPage], fetchActions)

fetchActions()

const router = useRouter()

const goToMeeting = action => {
  if (!action.meeting?.id) return
  router.push({ name: 'app-quality-committee-meetings-id', params: { id: action.meeting.id } })
}

// ── Close action ─────────────────────────────────────
// Create, edit and carry-forward stay in the meeting workspace where the
// agenda item and destination meeting exist — canManage/canCarryForward are
// deliberately left false on the shared table below.
const selectedAction = ref(null)
const isCloseDialogVisible = ref(false)

const onClose = action => {
  selectedAction.value = action
  isCloseDialogVisible.value = true
}

const onClosed = () => {
  fetchActions()
}
</script>

<template>
  <section>
    <VCard
      variant="flat"
      class="border mb-6"
    >
      <VCardText class="d-flex flex-wrap gap-4 align-center">
        <div
          class="flex-grow-1 flex-md-grow-0"
          style="min-inline-size: 12rem;"
        >
          <AppSelect
            v-model="committeeId"
            :items="committees"
            item-title="name"
            item-value="id"
            placeholder="Committee"
            density="compact"
            clearable
          />
        </div>

        <div
          class="flex-grow-1 flex-md-grow-0"
          style="min-inline-size: 14rem;"
        >
          <AppSelect
            v-model="status"
            :items="statusOptions"
            item-title="title"
            item-value="value"
            placeholder="Status"
            density="compact"
            multiple
            chips
            closable-chips
            clearable
          />
        </div>

        <div
          class="flex-grow-1 flex-md-grow-0"
          style="min-inline-size: 14rem;"
        >
          <AppSelect
            v-model="assigneeId"
            :items="assignees"
            item-title="name"
            item-value="id"
            placeholder="Assignee"
            density="compact"
            clearable
          />
        </div>

        <div
          class="flex-grow-1 flex-md-grow-0"
          style="min-inline-size: 14rem;"
        >
          <AppDateTimePicker
            v-model="dateRange"
            placeholder="Target Date Range"
            density="compact"
            :config="dateRangeConfig"
            clearable
          />
        </div>

        <div class="d-flex align-center">
          <VSwitch
            v-model="overdueOnly"
            label="Overdue only"
            density="compact"
            color="primary"
            hide-details
          />
        </div>
      </VCardText>
    </VCard>

    <VCard
      variant="flat"
      class="border"
    >
      <VCardItem>
        <template #title>
          <AppTextField
            placeholder="Search..."
            density="compact"
            style="inline-size: 280px;"
            @input="debouncedSearch($event.target.value)"
          >
            <template #prepend-inner>
              <VIcon
                icon="tabler-search"
                size="16"
              />
            </template>
          </AppTextField>
        </template>
        <template #append>
          <IconBtn
            variant="tonal"
            class="rounded"
            :loading="isFetching"
            @click="fetchActions"
          >
            <VIcon icon="tabler-refresh" />
            <VTooltip activator="parent">
              Refresh
            </VTooltip>
          </IconBtn>
        </template>
      </VCardItem>

      <VDivider />

      <VCardText>
        <CommitteeActionTable
          :actions="actions"
          :loading="isFetching"
          :error="hasFailed"
          :show-committee="true"
          :show-meeting="true"
          :can-manage="false"
          :can-close="canClose"
          :can-carry-forward="false"
          empty-text="No committee actions match these filters."
          @retry="fetchActions"
          @close="onClose"
          @open-meeting="goToMeeting"
        />
      </VCardText>

      <VDivider v-if="totalActions" />

      <div
        v-if="totalActions"
        class="d-flex align-center justify-sm-space-between justify-center flex-wrap gap-3 pa-5 pt-3"
      >
        <p class="text-sm text-medium-emphasis mb-0">
          {{ totalActions }} total records
        </p>
        <VPagination
          v-model="page"
          :length="Math.ceil(totalActions / itemsPerPage)"
          :total-visible="5"
        />
      </div>
    </VCard>

    <ActionCloseDialog
      v-model:is-dialog-visible="isCloseDialogVisible"
      :action="selectedAction"
      @closed="onClosed"
    />
  </section>
</template>
